import { useState, useEffect, useReducer, useContext } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const isUnauthorizedError = (error) =>
  error?.response?.status === 401 || error?.response?.status === 403;

const reducer = (state, action) => {
  if (action.type === "LOAD_WHATSAPPS") {
    const whatsApps = action.payload;
    return [...whatsApps];
  }

  if (action.type === "UPDATE_WHATSAPPS") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex] = whatsApp;
      return [...state];
    } else {
      return [whatsApp, ...state];
    }
  }

  if (action.type === "UPDATE_SESSION") {
    const whatsApp = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsApp.id);

    if (whatsAppIndex !== -1) {
      state[whatsAppIndex].status = whatsApp.status;
      state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
      state[whatsAppIndex].qrcode = whatsApp.qrcode;
      state[whatsAppIndex].retries = whatsApp.retries;
      return [...state];
    } else {
      return [...state];
    }
  }

  if (action.type === "DELETE_WHATSAPPS") {
    const whatsAppId = action.payload;
    const whatsAppIndex = state.findIndex((s) => s.id === whatsAppId);
    if (whatsAppIndex !== -1) {
      state.splice(whatsAppIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useWhatsApps = () => {
  const [whatsApps, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const { user, socket, isAuth, loading: authLoading } = useContext(AuthContext);

  // Effect para carregar os dados iniciais
  useEffect(() => {
    if (authLoading) {
      return;
    }

    if (!isAuth) {
      dispatch({ type: "RESET" });
      setLoading(false);
      return;
    }

    if (!user?.id || !user?.companyId) {
      dispatch({ type: "RESET" });
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchSession = async () => {
      try {
        const { data } = await api.get("/whatsapp/?session=0");
        dispatch({ type: "LOAD_WHATSAPPS", payload: data });
      } catch (err) {
        if (!isUnauthorizedError(err)) {
          console.error("Erro ao carregar WhatsApps:", err);
        }
        dispatch({ type: "RESET" });
      } finally {
        setLoading(false);
      }
    };

    fetchSession();
  }, [authLoading, isAuth, user?.id, user?.companyId]);

  // Effect para configurar os listeners do socket
  useEffect(() => {
    if (
      !user?.companyId ||
      !socket ||
      typeof socket.on !== "function" ||
      typeof socket.off !== "function"
    ) {
      return;
    }

    const companyId = user.companyId;

    const onCompanyWhatsapp = (data) => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
      }
    };

    const onCompanyWhatsappSession = (data) => {
      if (data.action === "update") {
        dispatch({ type: "UPDATE_SESSION", payload: data.session });
      }
    };

    const whatsappEvent = `company-${companyId}-whatsapp`;
    const sessionEvent = `company-${companyId}-whatsappSession`;

    socket.on(whatsappEvent, onCompanyWhatsapp);
    socket.on(sessionEvent, onCompanyWhatsappSession);

    return () => {
      socket.off(whatsappEvent, onCompanyWhatsapp);
      socket.off(sessionEvent, onCompanyWhatsappSession);
    };
  }, [socket, user?.companyId]);

  return { whatsApps, loading };
};

export default useWhatsApps;
